import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useChannel } from '@/context/ChannelContext'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { bookmarkStep, getOnboardingState } from '@/api/onboarding'
import { getLastPayment, type LastPaymentResponse } from '@/api/payment'
import { track } from '@/lib/analytics'
import { fireCelebration } from '@/lib/confetti'

type Mode = 'final' | 'payment-success' | 'loading'

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

function formatRub(n: number): string {
  return n.toLocaleString('ru-RU') + ' ₽'
}

function generateReceiptNumber(seed?: string): string {
  // Используем paidAt timestamp как seed для стабильного номера на одну сессию
  if (seed) {
    let hash = 0
    for (let i = 0; i < seed.length; i++) {
      hash = (hash * 31 + seed.charCodeAt(i)) | 0
    }
    return `#TGX-${Math.abs(hash).toString().padStart(8, '0').slice(0, 8)}`
  }
  const n = Math.floor(Math.random() * 90000000) + 10000000
  return `#TGX-${n}`
}

export function OnboardingFinalPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { channels } = useChannel()
  const [searchParams, setSearchParams] = useSearchParams()
  const [mode, setMode] = useState<Mode>('loading')
  const [paymentInfo, setPaymentInfo] = useState<LastPaymentResponse | null>(null)
  const pinnedChannelUUID = usePinnedChannelUUID()
  const pinnedChannel = channels.find((c) => c.uuid === pinnedChannelUUID)
  const channelTitle = pinnedChannel?.title || 'ваш канал'

  // На входе в payment-success режим — подтянуть детали последнего платежа
  useEffect(() => {
    if (mode !== 'payment-success' || !pinnedChannelUUID) return
    let cancelled = false
    getLastPayment(pinnedChannelUUID)
      .then((res) => {
        if (cancelled) return
        if (res.success) setPaymentInfo(res)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [mode, pinnedChannelUUID])

  // На маунт определяем режим:
  // - ?payment=success в URL проверяем через backend, а не верим query-param вслепую
  // - иначе → mode='final' (полный finale с конфетти). hasCampaigns на бэке может
  //   лагать после успешного create-campaigns, поэтому НЕ редиректим обратно
  //   на editor по этому флагу — это создаёт цикл «Завершить → /final → /editor».
  useEffect(() => {
    const justPaid = searchParams.get('payment') === 'success'
    if (justPaid) {
      if (!pinnedChannelUUID) {
        navigate('/onboarding/tariff?payment=cancelled', { replace: true })
        return
      }
      let cancelled = false
      setMode('loading')
      Promise.allSettled([getLastPayment(pinnedChannelUUID), getOnboardingState()]).then((results) => {
        if (cancelled) return
        const paymentResult = results[0]
        const onboardingResult = results[1]
        const payment = paymentResult.status === 'fulfilled' && paymentResult.value.success ? paymentResult.value : null
        const hasTariff = onboardingResult.status === 'fulfilled' && onboardingResult.value.hasTariff

        if (payment) setPaymentInfo(payment)

        if (payment || hasTariff) {
          const alreadyTracked = sessionStorage.getItem('tgx-payment-tracked') === '1'
          if (!alreadyTracked) {
            track('payment_success', { message: 'оплата прошла' })
            try { sessionStorage.setItem('tgx-payment-tracked', '1') } catch {}
          }
          setMode('payment-success')
          return
        }

        navigate('/onboarding/tariff?payment=cancelled', { replace: true })
      })
      return () => {
        cancelled = true
      }
    }
    if (searchParams.get('payment') === 'cancelled') {
      navigate('/onboarding/tariff?payment=cancelled', { replace: true })
      return
    }
    setMode('final')
  }, [navigate, pinnedChannelUUID, searchParams])

  // Конфетти на оба режима
  useEffect(() => {
    if (mode === 'loading') return
    fireCelebration()
    const t = setTimeout(() => fireCelebration(), 800)
    return () => clearTimeout(t)
  }, [mode])

  function handleContinueToCreatives() {
    // creatives-editor сам стартует AI-генерацию (читает посты канала через
    // /channel-creatives/generate). ad-gen-prepare — это fallback на случай
    // INSUFFICIENT_POSTS, туда юзера перебрасывает сама editor-страница.
    bookmarkStep('creatives-editor')
    if (searchParams.has('payment')) {
      const next = new URLSearchParams(searchParams)
      next.delete('payment')
      setSearchParams(next, { replace: true })
    }
    navigate('/onboarding/creatives-editor', { replace: true })
  }

  function handleOpenDashboard() {
    bookmarkStep(null)
    queryClient.invalidateQueries({ queryKey: ['channels'] })
    navigate('/dashboard?welcome=1')
  }

  if (mode === 'loading') {
    return (
      <OnboardingShell step="final" variant="final">
        <div style={{ minHeight: '60vh' }} />
      </OnboardingShell>
    )
  }

  if (mode === 'payment-success') {
    // Реальные данные если /payment/last вернул success, иначе ожидаемые fallback'ы.
    const isTrial = paymentInfo ? paymentInfo.abVariant === 'trial' : true
    const todayAmount = paymentInfo?.amount != null ? formatRub(paymentInfo.amount) : isTrial ? '1 ₽' : '4 900 ₽'
    const tariffLabel = isTrial ? 'Профи · триал 7 дней' : 'Профи · 1 месяц'
    const nextChargeDate = paymentInfo?.paidUntil ? formatDate(paymentInfo.paidUntil) : '—'
    const nextChargeAmount = paymentInfo?.nextChargeAmount != null ? formatRub(paymentInfo.nextChargeAmount) : '4 900 ₽'
    const isRecurrent = paymentInfo?.isRecurrentActive ?? true
    const receiptNum = generateReceiptNumber(paymentInfo?.paidAt)
    const paymentEmail = (() => {
      try { return sessionStorage.getItem('tgx-payment-email') || '' } catch { return '' }
    })()

    return (
      <OnboardingShell step="tariff" variant="full">
        <div className="paid-wrap">
          <div className="paid-check" aria-hidden>
            <svg viewBox="0 0 120 120">
              <circle className="paid-check-ring" cx="60" cy="60" r="54" fill="none" strokeWidth="4" />
              <circle className="paid-check-disc" cx="60" cy="60" r="44" fill="#00A860" />
              <path
                className="paid-check-tick"
                d="M40 62 L54 76 L82 46"
                fill="none"
                stroke="#fff"
                strokeWidth="7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p className="stage-eyebrow center">
            <span className="stage-eyebrow-pill success">Оплата прошла</span>
          </p>
          <h1 className="stage-title center">
            {isTrial ? (
              <>
                Триал <em>Профи</em> активирован.
              </>
            ) : (
              <>
                Тариф <em>Профи</em> активен.
              </>
            )}
          </h1>
          <p className="stage-sub center">
            Списали&nbsp;<b>{todayAmount}</b>. Все функции Профи доступны до&nbsp;<b>{nextChargeDate}</b>
            {isTrial && <> &nbsp;— потом по&nbsp;{nextChargeAmount}/мес, отменить можно в&nbsp;один клик</>}.
          </p>

          <div className="paid-receipt">
            <div className="paid-receipt-head">
              <div className="paid-receipt-title">Чек об&nbsp;оплате</div>
              <div className="paid-receipt-num">{receiptNum}</div>
            </div>
            <ul className="paid-receipt-rows">
              <li>
                <span>Тариф</span>
                <b>{tariffLabel}</b>
              </li>
              <li>
                <span>{isRecurrent ? 'Следующее списание' : 'Действует до'}</span>
                <b>{nextChargeDate}{isRecurrent && nextChargeAmount ? ` · ${nextChargeAmount}` : ''}</b>
              </li>
              <li className="paid-receipt-total">
                <span>Сегодня</span>
                <b>{todayAmount}</b>
              </li>
            </ul>
            <div className="paid-receipt-foot">
              <div className="paid-receipt-email">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16v16H4z" />
                  <path d="M4 8l8 6 8-6" />
                </svg>
                <span>
                  Чек отправлен на email{paymentEmail ? <> <b>{paymentEmail}</b></> : null}
                </span>
              </div>
            </div>
          </div>

          <ul className="paid-unlocks">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>Полная аналитика канала</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>Реклама в&nbsp;Я.Директе без&nbsp;комиссии агента</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>Лендинги, умные кнопки, антиспам</span>
            </li>
          </ul>

          <button className="paid-cta" onClick={handleContinueToCreatives}>
            Дальше&nbsp;— креативы
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <style>{`
          .paid-wrap {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            max-width: 580px;
            margin: 24px auto 0;
          }
          .paid-check {
            width: 96px;
            height: 96px;
            margin: 8px 0 22px;
          }
          .paid-check svg { width: 100%; height: 100%; }
          .paid-check-ring {
            stroke: rgba(0, 168, 96, 0.25);
            transform-origin: 60px 60px;
            animation: paid-ring 0.7s ease-out both;
          }
          .paid-check-disc {
            transform-origin: 60px 60px;
            animation: paid-disc 0.45s cubic-bezier(.34,1.56,.64,1) both;
          }
          .paid-check-tick {
            stroke-dasharray: 80;
            stroke-dashoffset: 80;
            animation: paid-tick 0.5s ease-out 0.35s forwards;
          }
          @keyframes paid-disc {
            from { transform: scale(0.4); opacity: 0; }
            to   { transform: scale(1);   opacity: 1; }
          }
          @keyframes paid-ring {
            from { transform: scale(0.6); opacity: 0; }
            to   { transform: scale(1.05); opacity: 1; }
          }
          @keyframes paid-tick {
            to { stroke-dashoffset: 0; }
          }

          .paid-receipt {
            width: 100%;
            margin-top: 28px;
            background: var(--tg-surface, #fff);
            border: 1px solid var(--tg-line-2, #f0f0f4);
            border-radius: 16px;
            text-align: left;
            overflow: hidden;
          }
          .paid-receipt-head {
            padding: 16px 22px;
            display: flex;
            align-items: baseline;
            border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
          }
          .paid-receipt-title {
            font-weight: 700;
            font-size: 14px;
            color: var(--tg-ink, #111);
          }
          .paid-receipt-num {
            margin-left: auto;
            font-size: 12px;
            font-variant-numeric: tabular-nums;
            color: var(--tg-mute, #8f9eb0);
            font-family: ui-monospace, SFMono-Regular, monospace;
          }
          .paid-receipt-rows {
            list-style: none;
            margin: 0;
            padding: 6px 22px 14px;
          }
          .paid-receipt-rows li {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            padding: 10px 0;
            font-size: 13px;
            border-bottom: 1px dashed var(--tg-line-2, #f0f0f4);
          }
          .paid-receipt-rows li:last-child { border-bottom: 0; }
          .paid-receipt-rows li span { color: var(--tg-mute, #8f9eb0); }
          .paid-receipt-rows li b { color: var(--tg-ink, #111); font-weight: 700; }
          .paid-receipt-total {
            margin-top: 4px;
            padding-top: 14px !important;
            border-top: 1px solid var(--tg-line-2, #f0f0f4) !important;
            border-bottom: 0 !important;
          }
          .paid-receipt-total span { color: var(--tg-ink, #111) !important; font-weight: 600; }
          .paid-receipt-total b {
            font-size: 18px !important;
            color: var(--tg-up, #00a860) !important;
          }
          .paid-receipt-foot {
            display: flex;
            gap: 8px;
            padding: 12px 18px;
            background: var(--tg-surface-2, #f5f5f9);
            border-top: 1px solid var(--tg-line-2, #f0f0f4);
          }
          .paid-receipt-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            background: var(--tg-surface, #fff);
            border: 1px solid var(--tg-line-2, #f0f0f4);
            border-radius: 8px;
            font-family: inherit;
            font-weight: 600;
            font-size: 12px;
            color: var(--tg-ink-2, #333);
            cursor: pointer;
          }
          .paid-receipt-link:hover {
            border-color: var(--tg-blue, #0080ff);
            color: var(--tg-blue-press, #0066cc);
            background: var(--tg-blue-bg, #ebf2ff);
          }
          .paid-receipt-link svg { width: 13px; height: 13px; }
          .paid-receipt-email {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12.5px;
            color: var(--tg-mute, #8f9eb0);
            line-height: 1.4;
          }
          .paid-receipt-email svg {
            width: 14px;
            height: 14px;
            color: var(--tg-up, #00a860);
            flex-shrink: 0;
          }
          .paid-receipt-email b {
            color: var(--tg-ink, #111);
            font-weight: 700;
          }

          .paid-unlocks {
            list-style: none;
            margin: 22px 0 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
          }
          .paid-unlocks li {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 14px;
            background: var(--tg-surface, #fff);
            border: 1px solid var(--tg-line-2, #f0f0f4);
            border-radius: 10px;
            font-size: 13px;
            color: var(--tg-ink-2, #333);
          }
          .paid-unlocks li svg {
            width: 14px;
            height: 14px;
            color: var(--tg-up, #00a860);
            flex-shrink: 0;
          }
          .paid-cta {
            margin-top: 28px;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 16px 28px;
            background: var(--tg-blue, #0080ff);
            color: white;
            border: 0;
            border-radius: 12px;
            font-family: inherit;
            font-weight: 700;
            font-size: 16px;
            cursor: pointer;
            box-shadow: 0 8px 24px -8px rgba(0,128,255,0.5);
          }
          .paid-cta:hover { background: var(--tg-blue-hover, #0066cc); }
          .paid-cta svg { width: 18px; height: 18px; }
        `}</style>
      </OnboardingShell>
    )
  }

  return (
    <OnboardingShell step="final" variant="final">
      <div className="welcome-wrap">
        <div>
          <p className="stage-eyebrow">
            <span className="stage-eyebrow-pill success">Готово</span>
            Проект собран
          </p>
          <h1 className="stage-title">
            Готово.
            <br />
            Кабинет&nbsp;на&nbsp;связи.
          </h1>
          <p className="stage-sub">
            Спасибо за&nbsp;оплату. Аналитика по&nbsp;охватам и&nbsp;вовлечённости появится в&nbsp;течение&nbsp;~24&nbsp;часов после первой синхронизации с&nbsp;MAX.
          </p>

          <ul className="welcome-checklist">
            <li className="welcome-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>
                Канал <b>«{channelTitle}»</b>&nbsp;подключён
              </span>
            </li>
            <li className="welcome-check">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              <span>
                Тариф <b>Профи</b> активен
              </span>
            </li>
          </ul>

          <div className="welcome-foot-cta">
            <button className="welcome-cta-primary" onClick={handleOpenDashboard}>
              Открыть кабинет
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <p className="welcome-tip">
            Полные метрики появятся через ~24 часа после первой синхронизации с MAX.
          </p>
        </div>

        <div className="welcome-preview">
          <div className="welcome-preview-bar">
            <span /> <span /> <span />
            <div className="welcome-preview-url">lk.telegraphyx.ru</div>
          </div>
          <div className="welcome-preview-body">
            <div className="welcome-preview-greet">
              Привет&nbsp;👋
            </div>
            <div className="welcome-preview-stats">
              <div className="welcome-preview-stat">
                <div className="welcome-preview-stat-label">Подписчики</div>
                <div className="welcome-preview-stat-val">5 132</div>
              </div>
              <div className="welcome-preview-stat">
                <div className="welcome-preview-stat-label">Расход / 7д</div>
                <div className="welcome-preview-stat-val">— ₽</div>
              </div>
              <div className="welcome-preview-stat">
                <div className="welcome-preview-stat-label">Аналитика</div>
                <div className="welcome-preview-stat-val">~24ч</div>
              </div>
            </div>
            <div className="welcome-preview-chart">
              <svg viewBox="0 0 280 60" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="welcome-chart-grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#0080FF" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#0080FF" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,55 L40,52 L80,48 L120,46 L160,42 L200,40 L240,38 L280,30 L280,60 L0,60 Z"
                  fill="url(#welcome-chart-grad)"
                />
                <path
                  d="M0,55 L40,52 L80,48 L120,46 L160,42 L200,40 L240,38 L280,30"
                  fill="none"
                  stroke="#0080FF"
                  strokeWidth="2"
                />
                <circle cx="280" cy="30" r="3" fill="#0080FF" />
              </svg>
              <div className="welcome-preview-chart-tip">данные через&nbsp;~24 часа</div>
            </div>
            <div className="welcome-preview-row">
              <div className="welcome-preview-row-dot green" />
              <div className="welcome-preview-row-text">Тариф Профи</div>
              <div className="welcome-preview-row-num">активен</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .welcome-wrap {
          display: grid;
          grid-template-columns: 1fr 480px;
          gap: 60px;
          max-width: 1180px;
          align-items: center;
          width: 100%;
          margin: 32px auto 0;
        }
        @media (max-width: 1024px) {
          .welcome-wrap { grid-template-columns: 1fr; }
        }
        .welcome-checklist {
          list-style: none;
          margin: 24px 0 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .welcome-check {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          font-size: 14px;
          color: var(--tg-ink-2, #333);
        }
        .welcome-check svg {
          width: 16px;
          height: 16px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
        }
        .welcome-check b { color: var(--tg-ink, #111); font-weight: 700; }
        .welcome-foot-cta {
          margin-top: 28px;
          display: flex;
          gap: 10px;
        }
        .welcome-cta-primary {
          padding: 14px 24px;
          background: var(--tg-blue, #0080ff);
          color: white;
          border: 0;
          border-radius: 10px;
          font-family: inherit;
          font-weight: 700;
          font-size: 15px;
          cursor: pointer;
          box-shadow: 0 4px 14px -4px rgba(0,128,255,0.5);
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .welcome-cta-primary:hover { background: var(--tg-blue-hover, #0066cc); }
        .welcome-cta-primary svg { width: 16px; height: 16px; }
        .welcome-tip {
          margin: 16px 0 0;
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
        }

        .welcome-preview {
          background: var(--tg-surface, #fff);
          border-radius: 12px;
          box-shadow: 0 24px 60px -16px rgba(38,40,45,0.18), 0 0 0 1px var(--tg-line-2, #f0f0f4);
          overflow: hidden;
          transform: rotate(-1deg);
          transform-origin: center;
        }
        .welcome-preview-bar {
          height: 32px;
          background: var(--tg-surface-2, #f5f5f9);
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 12px;
          border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        }
        .welcome-preview-bar > span {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          background: var(--tg-line-3, #d0d0d8);
        }
        .welcome-preview-url {
          margin-left: 12px;
          padding: 3px 10px;
          background: white;
          border-radius: 6px;
          font-size: 10px;
          color: var(--tg-mute, #8f9eb0);
          font-family: ui-monospace, SFMono-Regular, monospace;
        }
        .welcome-preview-body {
          padding: 18px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background:
            radial-gradient(circle at 0% 0%, var(--tg-blue-bg, #ebf2ff) 0%, transparent 60%),
            white;
        }
        .welcome-preview-greet {
          font-weight: 800;
          font-size: 17px;
          letter-spacing: -0.015em;
          color: var(--tg-ink, #111);
        }
        .welcome-preview-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .welcome-preview-stat {
          padding: 10px 12px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
        }
        .welcome-preview-stat-label {
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.06em;
          color: var(--tg-mute, #8f9eb0);
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .welcome-preview-stat-val {
          font-size: 17px;
          font-weight: 800;
          color: var(--tg-ink, #111);
          letter-spacing: -0.015em;
          font-variant-numeric: tabular-nums;
        }
        .welcome-preview-chart {
          height: 80px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          padding: 10px;
          position: relative;
        }
        .welcome-preview-chart svg { width: 100%; height: 100%; }
        .welcome-preview-chart-tip {
          position: absolute;
          top: 12px;
          right: 12px;
          font-size: 9px;
          color: var(--tg-mute, #8f9eb0);
          background: rgba(255,255,255,0.9);
          padding: 2px 6px;
          border-radius: 4px;
        }
        .welcome-preview-row {
          display: flex;
          gap: 8px;
          padding: 10px 12px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 8px;
          align-items: center;
          font-size: 11px;
        }
        .welcome-preview-row-dot {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          background: linear-gradient(135deg, var(--tg-blue, #0080ff), var(--tg-blue-press, #0066cc));
        }
        .welcome-preview-row-dot.green {
          background: linear-gradient(135deg, var(--tg-up, #00a860), #00805a);
        }
        .welcome-preview-row-text {
          flex: 1;
          color: var(--tg-ink-2, #333);
          font-weight: 600;
        }
        .welcome-preview-row-num {
          font-weight: 700;
          color: var(--tg-up, #00a860);
          font-variant-numeric: tabular-nums;
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.04em;
        }
      `}</style>
    </OnboardingShell>
  )
}

